/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AfterViewInit, ChangeDetectorRef, Component } from '@angular/core';
import { BaseComponent } from '../../../../../core/components';
import { DropdownModel, ErrorResponseModel } from '../../../../../core/models';
import { FIPApiService } from '../../http/fip.api.service';
import { FipModel } from '../../models';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IpGroupApiService, LockApiService } from '../../http';
import {
    LoadingService,
    LogService,
    ModalService,
    NotifyService,
    TransactionService,
    UtilsService
} from '../../../../../core/services';
import { concatMap, takeUntil } from 'rxjs/operators';
import { forkJoin, from, Observable } from 'rxjs';

@Component({
    selector: 'app-lb-modals-add-fips',
    templateUrl: './multipleFloatingIps.modal.html',
    styleUrls: ['./multipleFloatingIps.modal.scss']
})

export class MultipleFloatingIpsModalComponent extends BaseComponent implements AfterViewInit {
    /**
     * @External Array to push missing IPs for missing IPs modal
     */
    public missingIpReferences: any[] = [];

    /**
     * @External callback function from parent component.
     */
    public callBack: (...args: any[]) => void;

    /**
     * Store list of floating IPs
     */
    public fips: any = [];

    /**
     * Store list of fip groups
     */
    public ipGroups: any = [];

    /**
     * Store selected row object for table state checks.
     */
    public selectedRows: any = [];

    /**
     * Missing IPs form
     */
    public missingIpForm: any = new FormArray([]);

    /**
     * Dropdowns for the page.
     */
    public dropdown: any = {
        groups: []
    };

    /**
     * Modal State
     */
    public showModal: boolean = false;

    /**
     * IP submission error flag.
     */
    public modalHasError: boolean = false;

    constructor(
        private cdr: ChangeDetectorRef,
        private fipApiService: FIPApiService,
        private formBuilder: FormBuilder,
        private ipGroupApiService: IpGroupApiService,
        private loadingService: LoadingService,
        private lockApiService: LockApiService,
        private logService: LogService,
        private modalService: ModalService,
        private notifyService: NotifyService,
        private transactionService: TransactionService,
        private utilsService: UtilsService
    ) {
        super();
        this.setupFormGroup();
    }

    ngAfterViewInit(): void {
        forkJoin([
            this.fipApiService.getFloatingIPs(),
            this.ipGroupApiService.getGroups()
        ])
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroup): void => {
                    this.fips = dataGroup[0];
                    this.ipGroups = dataGroup[1];
                    this.initPage();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('MultipleFloatingIpsModalComponent: ngAfterViewInit', error.message);
                }
            });
    }

    /**
     * Select all rows to enable / disable.
     */
    public selectRows(): void {
        const controls = this.missingIpForm.get('rows').controls;
        const state = this.missingIpForm.get('selectAll').value;
        for (const control of controls) {
            control.get('enabled').setValue(!state);
        }
    }

    /**
     * Select row count.
     */
    public selectRowCount(): boolean {
        const rows = this.missingIpForm.get('rows').value;
        const enabledRows = rows.filter((item: { enabled: boolean; }) => item.enabled);

        return enabledRows.length !== rows.length && enabledRows.length > 0;
    }

    /**
     * get rows.
     */
    public getRows(): any {
        return this.missingIpForm.get('rows').controls as FormArray;
    }

    /**
     * Return fip state
     * @param state
     */
    public fipState(state: string): string {
        return state ? 'active' : 'inactive';
    }

    /**
     * Submits an API for each missing IP.
     */
    public submitNewIps(): Observable<any> {
        return new Observable((): void => {
            this.notifyService.muteLocalNotifications();
            const formValues = this.missingIpForm.get('rows').value;
            let counter: number = 0;
            let fipIndexReference: number;
            this.transactionService.startTransaction()
                .pipe(
                    concatMap(() =>
                        from(formValues || [])
                            .pipe(concatMap((ipData: any) => {
                                fipIndexReference = ipData.index;

                                return this.fipApiService.addIP(ipData);
                            }))
                    ),
                    takeUntil(this.destroyed$)
                )
                .subscribe({
                    next: (response: FipModel): void => {
                        this.missingIpReferences[fipIndexReference].id = response.id;
                        if (counter === formValues.length - 1) {
                            this.notifyService.unmuteLocalNotifications();
                            this.commitTransaction(this.missingIpReferences);
                        }
                        counter++;
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.rollbackHelper(counter, error);
                    }
                });
        });
    }

    /**
     * Rollback helper.
     * @param counter
     * @param error
     * @private
     */
    private rollbackHelper(counter: number, error: ErrorResponseModel): void {
        this.lockApiService.pollForLockStatus()
            .pipe(
                concatMap(() => this.transactionService.rollbackTransaction()),
                takeUntil(this.destroyed$)
            )
            .subscribe({
                next: () => {
                    this.loadingService.loadingButtonEmitter.next(false);
                    this.displayErrorInForm(error.error.errors, counter);
                    this.modalHasError = true;
                },
                error: () => {
                    this.logService.error('MultipleFloatingIps: rollback helper error ', error.message);
                    this.loadingService.loadingButtonEmitter.next(false);
                }
            });
    }

    /**
     * Commit transaction.
     * @param fipIds
     * @private
     */
    private commitTransaction(fipIds: any): void {
        this.transactionService.commitTransaction()
            .subscribe({
                next: (): void => {
                    this.callBack(fipIds);
                    this.modalService.close('missingIps');
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('MultipleFloatingIps: commitTransaction() error committing transaction ', error.message);
                }
            });
    }

    /**
     * Sets error on the form
     * @param errors
     * @param counter
     * @private
     */
    private displayErrorInForm(errors: any, counter: number): void {
        if (errors?.length) {
            const groupField = this.utilsService.getObjectValue(
                this.missingIpForm.get('rows'), `controls[${counter}].controls.groupId`
            );
            if (groupField) {
                groupField.setErrors({ 'responseError': errors[0].message });
                groupField.markAsTouched();
            }
        }
    }

    /**
     * Set up the main form group.
     * @private
     */
    private setupFormGroup(): void {
        this.missingIpForm = this.formBuilder.group({
            selectAll: [false],
            rows: this.formBuilder.array([])
        });
        this.missingIpForm.get('rows').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (values: any[]): void => {
                    const partialSelect: boolean = values.some(item => item.enabled);
                    this.missingIpForm.get('selectAll').setValue(partialSelect);
                }
            });
    }

    /**
     * Initialise page.
     * @private
     */
    private initPage(): void {
        this.showModal = true;
        this.setUpDropdownContent();
        const rows: FormArray = this.missingIpForm.get('rows') as FormArray;
        let index: number = 0;
        for (const ipReference of this.missingIpReferences) {
            if (ipReference.id === undefined) {
                const ipObject = this.fips.find((item: { ip: string; }): boolean => item.ip === ipReference.ip);
                rows.push(this.createFormControlRows(ipReference.ip, index));
                this.selectedRows.push(ipObject);
            }

            index++;
        }
        this.cdr.detectChanges();
        this.modalService.openModalInParent('missingIps');
    }

    /**
     * Create a form control for each missing IP.
     * @param ip
     * @param index
     * @private
     */
    private createFormControlRows(ip: string, index: number): FormGroup {
        return this.formBuilder.group({
            index: [index],
            ip: [ip],
            groupId: [null, Validators.required],
            enabled: [true]
        });
    }

    /**
     * Set up the dropdown array from data.
     */
    private setUpDropdownContent(): void {
        const groups: DropdownModel[] = [];
        for (const group of this.ipGroups) {
            groups.push(<DropdownModel>{
                key: group.id,
                value: group.name
            });
        }
        this.dropdown.groups = groups;
    }
}
